import axios from 'axios';

// 默认用相对路径 /api/v1 → 经 Vite dev server 代理到后端容器,
// 这样不论本地还是服务器(任意IP/域名)前端都不需要改地址。
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// JWT interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // 上传 FormData 时必须把默认的 application/json 删掉。
  // axios 实例上设了全局 Content-Type: application/json, 它会盖掉 axios 对
  // FormData 的自动处理 —— 浏览器就不再补 multipart/form-data 和 boundary,
  // 后端 DRF 收到 application/json 却解析不出 multipart, 直接回 415
  // "不支持请求中的媒体类型 application/json"。
  // 删掉这个头之后浏览器会自己带上正确的 Content-Type 与 boundary。
  // 以前是在每个上传点单独规避(见反演页那处注释), 漏一个就复现一次, 故收到这里统一处理。
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    // 登录失败不是旧会话过期；保留原始错误交给登录表单显示。
    if (originalRequest?.url?.includes('/auth/login/')) {
      return Promise.reject(err);
    }
    if (err.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        const { data } = await axios.post(`${API_BASE}/auth/refresh/`, { refresh });
        localStorage.setItem('access_token', data.access);
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // 正在登录页时不要刷新页面，否则提交表单的错误提示会被抹掉。
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

const firstApiErrorText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const text = value.trim();
    return text && text.length <= 500 ? text : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = firstApiErrorText(item);
      if (text) return text;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const text = firstApiErrorText(item);
      if (text) return text;
    }
  }
  return null;
};

const firstApiFieldError = (
  value: unknown,
  path: string[] = [],
): { path: string; text: string } | null => {
  if (typeof value === 'string') {
    const text = value.trim();
    return text && text.length <= 500 ? { path: path.join('.'), text } : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const result = firstApiFieldError(item, path);
      if (result) return result;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      const result = firstApiFieldError(item, [...path, key]);
      if (result) return result;
    }
  }
  return null;
};

export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    const text = data.trim();
    const isHtml = /^<!doctype html/i.test(text) || /<html[\s>]/i.test(text);
    if (text && !isHtml && text.length <= 500) return text;
  }
  const fieldError = firstApiFieldError(data?.field_errors);
  if (fieldError) return fieldError.path
    ? `${fieldError.path}：${fieldError.text}`
    : fieldError.text;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  return fallback;
};

// ── Auth ──
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/auth/login/', { username, password }),
  // register 已移除: 后端 /auth/register/ 一并下线, 账号由管理员开通
  profile: () => api.get('/auth/profile/'),
  updateProfile: (data: Record<string, string>) =>
    api.patch('/auth/profile/', data),
};

// ── Projects ──
export const projectAPI = {
  list: (params?: Record<string, string>) =>
    api.get('/projects/', { params }),
  get: (id: number) => api.get(`/projects/${id}/`),
  create: (data: Record<string, unknown>) =>
    api.post('/projects/', data),
  update: (id: number, data: Record<string, unknown>) =>
    api.patch(`/projects/${id}/`, data),
  delete: (id: number) => api.delete(`/projects/${id}/`),
};

// ── Formation ──
export const formationAPI = {
  getLayers: (projectId: number) =>
    api.get('/formation/layers/', { params: { project: projectId } }),
  getNaturalFractures: (projectId: number) =>
    api.get('/formation/fractures/', { params: { project: projectId } }),
  getAllNaturalFractures: async (projectId: number) => {
    const firstResponse = await api.get('/formation/fractures/', { params: { project: projectId, page: 1 } });
    const first = firstResponse.data as { count?: number; results?: unknown[]; next?: string | null } | unknown[];
    if (Array.isArray(first)) return { data: first };
    const results = Array.isArray(first.results) ? [...first.results] : [];
    const pageSize = results.length;
    const count = Number(first.count);
    if (Number.isFinite(count) && count > results.length && pageSize > 0) {
      const totalPages = Math.ceil(count / pageSize);
      // Bounded parallel reads avoid hundreds of serial round trips while
      // keeping the geology endpoint and its source records untouched.
      for (let start = 2; start <= totalPages; start += 8) {
        const pageNumbers = Array.from({ length: Math.min(8, totalPages - start + 1) }, (_, index) => start + index);
        const responses = await Promise.all(pageNumbers.map((page) => api.get(
          '/formation/fractures/', { params: { project: projectId, page } },
        )));
        responses.forEach((response) => {
          const payload = response.data as { results?: unknown[] };
          if (Array.isArray(payload.results)) results.push(...payload.results);
        });
      }
    }
    return { data: results };
  },
  batchImport: (data: { project_id: number; layers: unknown[] }) =>
    api.post('/formation/layers/batch_import/', data),
  // 层位标记 (Tops)
  listTops: (projectId: number) =>
    api.get('/formation/tops/', { params: { project: projectId } }),
  createTop: (body: { project: number; name: string; depth: number; marker_type?: string }) =>
    api.post('/formation/tops/', body),
  updateTop: (id: number, body: Partial<{ name: string; depth: number; marker_type: string }>) =>
    api.patch(`/formation/tops/${id}/`, body),
  deleteTop: (id: number) =>
    api.delete(`/formation/tops/${id}/`),

  // ── 天然裂缝 (成像测井 / 蚂蚁体解释成果) ──
  listFractures: (projectId: number) =>
    api.get('/formation/fractures/', { params: { project: projectId } }),
  importFractures: (data: { project_id: number; fractures: unknown[]; source?: string }) =>
    api.post('/formation/fractures/batch_import/', data),
  fractureStats: (projectId: number, opts?: { shmax?: number; rose_bins?: number; density_window?: number }) =>
    api.get('/formation/fractures/stats/', { params: { project: projectId, ...opts } }),
  deleteFracture: (id: number) =>
    api.delete(`/formation/fractures/${id}/`),

  // ── 断层面 (地震构造解释) ──
  listFaults: (projectId: number) =>
    api.get('/formation/faults/', { params: { project: projectId } }),
  createFault: (body: Record<string, unknown>) =>
    api.post('/formation/faults/', body),
  updateFault: (id: number, body: Record<string, unknown>) =>
    api.patch(`/formation/faults/${id}/`, body),
  deleteFault: (id: number) =>
    api.delete(`/formation/faults/${id}/`),

  // ── 层位面 (网格化解释面, 非地震体数据) ──
  listHorizons: (projectId: number) =>
    api.get('/formation/horizons/', { params: { project: projectId } }),
  createHorizon: (body: Record<string, unknown>) =>
    api.post('/formation/horizons/', body),
  deleteHorizon: (id: number) =>
    api.delete(`/formation/horizons/${id}/`),
};

// ── Wellbore ──
export const wellboreAPI = {
  getSurvey: (projectId: number) =>
    api.get('/wellbore/survey/', { params: { project: projectId } }),
  batchImportSurvey: (data: { project_id: number; stations: unknown[] }) =>
    api.post('/wellbore/survey/batch_import/', data),
  get3DTrajectory: (projectId: number) =>
    api.get('/wellbore/survey/trajectory_3d/', { params: { project: projectId } }),
  getAvoidanceZones: (projectId: number) =>
    api.get('/wellbore/avoidance/', { params: { project: projectId } }),
};

// ── 物模实验库 ──
export type LabField = { key: string; label: string; unit: string; kind: 'number' | 'text'; required: boolean };
export type LabExperimentType = {
  key: string; label: string; description: string;
  common: LabField[]; conditions: LabField[]; results: LabField[];
};
export type LabExperiment = {
  id: number; experiment_type: string; code: string; name: string; lab: string; test_date: string;
  formation: string; lithology: string; sample_id: string; notes: string; source_file: string;
  conditions: Record<string, number | string>; results: Record<string, number | string>;
  imported_by_name: string; updated_at: string;
};
export type LabImportResult = {
  created: number; updated: number; error_count: number;
  errors: Array<{ row: number; code: string; errors: string[] }>; unknown_headers: string[];
};
export type LabRecommendation = {
  experiment_type: string;
  target: Record<string, number | string>;
  matched_fields: string[];
  similar: Array<Pick<LabExperiment, 'id' | 'code' | 'name' | 'formation' | 'lithology' | 'lab' | 'conditions' | 'results'> & { similarity: number }>;
  guidance: string[];
  ranking: Array<{ proppant_type: string; mesh: string; concentration: number | null; conductivity_at_target: number; extrapolated: boolean; points: number }>;
  conductivity_tables: unknown[];
  calibrated: Record<string, number>;
};

export const labAPI = {
  schema: () => api.get<LabExperimentType[]>('/labdata/schema/'),
  list: (type: string, q?: string) => api.get<LabExperiment[]>('/labdata/experiments/', { params: { type, ...(q ? { q } : {}) } }),
  remove: (id: number) => api.delete(`/labdata/experiments/${id}/`),
  importFile: (type: string, file: File) => {
    const form = new FormData();
    form.append('type', type);
    form.append('file', file);
    return api.post<LabImportResult>('/labdata/experiments/import/', form, { timeout: 120000 });
  },
  template: (type: string) => api.get<Blob>('/labdata/template/', { params: { type }, responseType: 'blob' }),
  projectConditions: (projectId: number, top?: number | null, bottom?: number | null) =>
    api.get<{ conditions: Record<string, number>; sample_count: number; notes: string[] }>('/labdata/project-conditions/', {
      params: { project: projectId, ...(top != null ? { top } : {}), ...(bottom != null ? { bottom } : {}) },
    }),
  recommend: (type: string, target: Record<string, number | string>, limit = 8) =>
    api.post<LabRecommendation>('/labdata/recommend/', { type, target, limit }),
};

// ── 成像测井识别 (井壁稳定) ──
export type ImageLogPickStatus = 'candidate' | 'accepted' | 'rejected';
export type ImageLogFracture = {
  id: number; depth: number; y0_px: number; amplitude_px: number; phase: number;
  dip: number; azimuth: number | null; coverage: number;
  type: 'open' | 'filled' | 'healed' | 'induced' | 'bedding'; status: ImageLogPickStatus;
};
export type ImageLogBoreholeFeature = {
  id: number; type: 'breakout' | 'induced_fracture'; top_depth: number; bottom_depth: number;
  top_px: number; bottom_px: number; azimuth: number; width: number; shmax_azimuth: number;
  status: ImageLogPickStatus;
};
export type ImageLogParams = {
  top_depth: number; bottom_depth: number; diameter_mm: number; start_azimuth: number;
  dark_features: boolean; crop_left: number; crop_right: number; crop_top: number; crop_bottom: number;
  min_coverage: number; max_dip: number; page: number;
};
export type ImageLogSummary = {
  fracture_count: number; open_fracture_count: number; breakout_count: number;
  breakout_length_m: number; induced_fracture_count: number; shmax_azimuth: number | null;
};
export type ImageLogImages = { work: string; clahe: string; binary: string; edges: string };
export type ImageLogRecord = {
  id: number; project: number; name: string; original_filename: string; page_count: number;
  params: ImageLogParams; work_width: number; work_height: number;
  fractures: ImageLogFracture[]; borehole_features: ImageLogBoreholeFeature[]; summary: ImageLogSummary;
  published_fracture_ids: number[]; published_at: string | null; updated_at: string;
  images?: ImageLogImages;
};
export type ImageLogListItem = Pick<ImageLogRecord, 'id' | 'name' | 'original_filename' | 'summary' | 'published_at' | 'updated_at'>;

export const imageLogAPI = {
  list: (projectId: number) => api.get<ImageLogListItem[]>('/wellbore/image-logs/', { params: { project: projectId } }),
  get: (id: number) => api.get<ImageLogRecord>(`/wellbore/image-logs/${id}/`),
  images: (id: number) => api.get<ImageLogImages>(`/wellbore/image-logs/${id}/images/`, { timeout: 120000 }),
  upload: (projectId: number, file: File, params: Partial<ImageLogParams>) => {
    const form = new FormData();
    form.append('project', String(projectId));
    form.append('file', file);
    Object.entries(params).forEach(([key, value]) => form.append(key, String(value)));
    return api.post<ImageLogRecord>('/wellbore/image-logs/', form, { timeout: 180000 });
  },
  reanalyze: (id: number, params: Partial<ImageLogParams>) =>
    api.post<ImageLogRecord>(`/wellbore/image-logs/${id}/reanalyze/`, params, { timeout: 180000 }),
  review: (id: number, data: {
    fractures?: Array<Partial<ImageLogFracture> & { id: number }>;
    borehole_features?: Array<Partial<ImageLogBoreholeFeature> & { id: number }>;
  }) => api.patch<ImageLogRecord>(`/wellbore/image-logs/${id}/`, data),
  publish: (id: number) => api.post<{ published: number; fracture_ids: number[] }>(`/wellbore/image-logs/${id}/publish/`),
  remove: (id: number) => api.delete(`/wellbore/image-logs/${id}/`),
};

// ── Pressure ──
export type PressurePoint = {
  id: number;
  depth: number;
  pore_pressure: number | null;
  pore_pressure_gradient: number | null;
  pore_pressure_emw: number | null;
  collapse_pressure: number | null;
  collapse_pressure_gradient: number | null;
  collapse_pressure_emw: number | null;
  fracture_pressure: number | null;
  fracture_pressure_gradient: number | null;
  fracture_pressure_emw: number | null;
  overburden_pressure: number | null;
  vertical_stress: number | null;
  vertical_stress_gradient: number | null;
  safe_window_lower_pressure: number | null;
  safe_window_upper_pressure: number | null;
  safe_window_lower_emw: number | null;
  safe_window_upper_emw: number | null;
  safe_window_lower_control_code: number | null;
  safe_window_upper_control_code: number | null;
  safe_window_missing_code: number | null;
  safe_window_risk_code: number | null;
  depth_reference: 'MD' | 'TVD' | 'TVDSS' | string;
  source_run_id: number | null;
};

export const pressureAPI = {
  getProfile: (projectId: number) =>
    api.get<PressurePoint[] | { results: PressurePoint[] }>('/pressure/', {
      params: { project: projectId },
    }),
  batchImport: (data: { project_id: number; points: unknown[] }) =>
    api.post('/pressure/batch_import/', data),
};

// ── Sweet Spot ──
export const sweetSpotAPI = {
  list: (projectId: number) =>
    api.get('/sweetspot/', { params: { project: projectId } }),
  batchImport: (data: { project_id: number; evaluations: unknown[] }) =>
    api.post('/sweetspot/batch_import/', data),
};

// ── Logging (well-log curves) ──
export const loggingAPI = {
  getCurves: (projectId: number) =>
    api.get('/logging/curves/', { params: { project: projectId } }),
  getInterpretation: (projectId: number) =>
    api.get('/logging/interpretation/', { params: { project: projectId } }),
  batchImportCurves: (data: { project_id: number; curves: unknown[] }) =>
    api.post('/logging/curves/batch_import/', data),
};

// ── DataHub (unified import / preprocess / publish) ──
export type DataHubType =
  | 'well_log'
  | 'mud_log'
  | 'survey'
  | 'formation'
  | 'pressure'
  | 'mechanics'
  | 'lab_test'
  | 'pressure_test'
  | 'wellbore_event'
  | 'mud_program'
  | 'fracture'
  | 'microseismic'
  | 'seismic'
  | 'other';

export type DataHubColumn = {
  source_key: string;
  display_name: string;
  unit?: string;
  standard_field?: string | null;
  standard_label?: string;
  target_unit?: string;
  conversion?: string;
  unit_options?: string[];
  unit_status?: 'declared' | 'inferred' | 'missing' | 'confirmed';
  value_type?: 'number' | 'text' | 'boolean';
  match_confidence?: number;
  match_reason?: string;
  confirmation_required?: boolean;
  valid_count?: number;
  missing_count?: number;
  valid_rate?: number;
  range?: [number, number] | null;
  dataset_field?: string;
  source_unit?: string;
  unit_source?: 'HEADER' | 'UNIT_ROW' | 'LAS_HEADER' | 'UNRESOLVED';
  pressure_role?: 'WELLHEAD' | 'BOTTOMHOLE' | 'CASING' | 'ANNULUS' | 'PUMP' | 'UNKNOWN' | 'NOT_PRESSURE';
  pressure_role_confidence?: 'High' | 'Medium' | 'Low';
  rate_role?: 'TOTAL_SLURRY' | 'CARRIER_FLUID' | 'PUMP_RATE' | 'UNKNOWN' | 'NOT_RATE';
  rate_role_confidence?: 'High' | 'Medium' | 'Low';
  auxiliary_channel?: boolean;
  raw_label?: string;
  alias_status?: 'RESOLVED' | 'ALIAS_AMBIGUOUS';
  candidate_fields?: string[];
  candidate_roles?: string[];
};

export type RealWellAcceptanceStatus = 'PASS' | 'PASS_WITH_WARNINGS' | 'REVIEW_REQUIRED' | 'FAIL';

export type RealWellStageAcceptance = {
  stage: number | string | null;
  rows: number;
  time: string;
  pressure: string;
  rate: string;
  pressureReference: string;
  rateSource: Record<string, any>;
  stepCount: number;
  interpretation: string;
  fieldFriction: {
    status: string;
    confidence?: string;
    registryVersion?: number;
    validRateRange: number[];
    monotonic: boolean | null;
    diagnostics: string[];
  };
  bhp: {
    status: string;
    pressureClosureErrorMpa?: number | null;
    measuredBhpMetrics?: Record<string, number | null> | null;
    representativePoints?: Array<Record<string, any>>;
    diagnostics: string[];
  };
  status: RealWellAcceptanceStatus;
  diagnostics: string[];
};

export type RealWellAcceptance = {
  profile: {
    datasetId: number;
    datasetVersion: number;
    wellId?: string | null;
    stageCount: number;
    sourceFile: string;
    vendorTemplate: string;
    templateId: string;
    templateVersion: string;
    dataSource: 'SYNTHETIC FIXTURE' | 'FIELD FILE';
    sheetName: string;
    headerRow?: number | null;
    headerConfidence: string;
    pressureReference: string;
    pressureRoleConfidence: string;
    rateRoleConfidence: string;
    timeSource: string;
    unitStatus: string;
    stageMappingStatus: string;
    qcStatus: string;
    interpretationStatus: string;
    hydraulicStatus: string;
    acceptanceStatus: RealWellAcceptanceStatus;
    warnings: string[];
    performanceSummary: Record<string, number>;
    provenance: Record<string, any>;
  };
  dimensions: Record<string, string>;
  stageAcceptance: RealWellStageAcceptance[];
  diagnostics: Array<{ code: string; count: number }>;
  intakeChecklist: Array<{ code: string; status: string }>;
  overallAcceptance: RealWellAcceptanceStatus;
};

export type DataHubSource = {
  id: number;
  project: number;
  name: string;
  data_type: DataHubType;
  original_filename: string;
  file_format: string;
  file_size: number;
  file_sha256?: string;
  original_row_count: number;
  is_complete: boolean;
  status: 'uploaded' | 'parsed' | 'processed' | 'failed';
  row_count: number;
  columns: DataHubColumn[];
  preview_rows: Record<string, unknown>[];
  parse_warnings: string[];
  error_message?: string;
  meta?: Record<string, any>;
  latest_dataset_id?: number | null;
  dataset_count?: number;
  applied_dataset_count?: number;
  can_delete?: boolean;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type DataHubDataset = {
  id: number;
  project: number;
  source: number;
  source_name: string;
  source_file?: string;
  name: string;
  data_type: DataHubType;
  version: number;
  status: 'ready' | 'published' | 'failed';
  row_count: number;
  columns: DataHubColumn[];
  preview_rows?: Record<string, unknown>[];
  summary: Record<string, any>;
  quality_score: number;
  content_checksum?: string;
  review_revision?: number;
  reviewed_at?: string | null;
  has_applied_data?: boolean;
  can_delete?: boolean;
  archived_at?: string | null;
  published_to?: string;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type DataHubApplication = {
  id: number;
  project: number;
  dataset: number;
  dataset_name: string;
  dataset_version: number;
  dataset_checksum: string;
  status: 'processing' | 'applied' | 'failed';
  requested_by_name?: string | null;
  result: Record<string, any>;
  error_message: string;
  applied_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type DataHubBusinessInventoryModule = {
  code: string;
  label: string;
  route: string;
  unit: string;
  total_records: number;
  datahub_records: number;
  legacy_records: number;
  legacy_detail?: {
    min_depth?: number | null;
    max_depth?: number | null;
    fields?: Array<{ field: string; label: string; count: number }>;
    note?: string;
  } | null;
};

export type DataHubIssue = {
  id: number;
  row_index: number | null;
  field: string;
  depth: number | null;
  time_s: number | null;
  issue_type: string;
  severity: string;
  action: string;
  raw_value: string;
  cleaned_value: string;
  message: string;
};

export type DataHubAuditIssue = Pick<DataHubIssue, 'row_index' | 'field' | 'issue_type' | 'severity' | 'action' | 'raw_value' | 'cleaned_value' | 'message'>;

export type DataHubAuditRow = {
  row_index: number;
  display_index: number;
  raw: Record<string, unknown>;
  processed: Record<string, unknown>;
  issues: DataHubAuditIssue[];
  decisions?: Record<string, 'accepted' | 'raw' | 'modified' | 'excluded'>;
};

export type DataHubAuditResponse = {
  id: number;
  row_count: number;
  processed_row_count: number;
  raw_row_count: number;
  columns: DataHubColumn[];
  options: Record<string, any>;
  row_alignment?: 'source' | 'resampled';
  offset: number;
  limit: number;
  rows: DataHubAuditRow[];
};

export type DataHubProcessOptions = {
  mapping?: Array<Partial<DataHubColumn> & { source_key: string; dataset_field: string }>;
  range_action?: 'null' | 'clip' | 'keep';
  statistical_method?: 'none' | 'hampel' | 'iqr' | 'zscore';
  statistical_action?: 'null' | 'clip' | 'keep';
  smoothing?: { method: 'none' | 'mean' | 'median'; window: number };
  resample?: { enabled: boolean; axis?: string; step?: number };
  depth_reference?: 'MD' | 'TVD' | 'TVDSS' | '';
  pressure_reference_location?: 'WELLHEAD' | 'BOTTOMHOLE' | 'PRE_PERFORATION' | 'FRACTURE_ENTRY' | 'UNKNOWN';
  primary_pressure_source_key?: string;
  primary_rate_source_key?: string;
  manual_stage_number?: number;
  stage_windows?: Array<{ stage_number: number; start_time_s: number; end_time_s: number }>;
  template_id?: string;
  max_header_scan_rows?: number;
};

export type DataHubReadinessItem = {
  code: string;
  title: string;
  detail: string;
  blocking: boolean;
};

export type DataHubReadiness = {
  dataset_id: number;
  dataset_version: number;
  content_checksum: string;
  usage_mode: 'application' | 'workbench_input';
  ready_for_use: boolean;
  can_apply: boolean;
  already_applied: boolean;
  blockers: DataHubReadinessItem[];
  warnings: DataHubReadinessItem[];
  passed: DataHubReadinessItem[];
  quality: {
    score: number;
    issue_count: number;
    issue_counts: Record<string, number>;
    reviewed_cells: number;
    unresolved_critical: number;
  };
  downstream: Array<{ code: string; label: string; status: 'ready' | 'waiting' | 'blocked'; route: string; detail: string }>;
  latest_application?: { id: number; status: string; applied_at?: string | null; requested_by?: string; error_message?: string } | null;
};

export type DataHubCurvePoint = {
  row_index: number;
  display_index: number;
  axis: number;
  raw_original: number | null;
  raw: number | null;
  processed: number | null;
  issues: Array<Pick<DataHubIssue, 'issue_type' | 'severity' | 'action' | 'message' | 'raw_value' | 'cleaned_value'>>;
  decision?: 'accepted' | 'raw' | 'modified' | 'excluded';
};

export type DataHubCurveResponse = {
  id: number;
  field: string;
  field_label: string;
  unit: string;
  source_unit: string;
  axis: 'depth' | 'md' | 'time_s';
  axis_unit: string;
  row_count: number;
  sampled_count: number;
  visualizationSampled: boolean;
  originalPointCount: number;
  displayPointCount: number;
  issue_count?: number;
  issue_point_count?: number;
  reviewed_issue_count?: number;
  unresolved_issue_count?: number;
  issue_sampled?: number;
  row_alignment: 'source' | 'resampled';
  points: DataHubCurvePoint[];
};

export const datahubAPI = {
  listSources: (projectId: number, includeArchived = false) =>
    api.get<DataHubSource[]>('/datahub/sources/', { params: { project: projectId, include_archived: includeArchived ? 1 : undefined } }),
  getBusinessInventory: (projectId: number) =>
    api.get<{ project_id: number; modules: DataHubBusinessInventoryModule[] }>(
      '/datahub/sources/business-inventory/', { params: { project: projectId } }
    ),
  getSource: (id: number) =>
    api.get<DataHubSource>(`/datahub/sources/${id}/`),
  archiveSource: (id: number) =>
    api.post<DataHubSource>(`/datahub/sources/${id}/archive/`),
  restoreSource: (id: number) =>
    api.post<DataHubSource>(`/datahub/sources/${id}/restore/`),
  deleteSource: (id: number) =>
    api.post<{ deleted: true; source_id: number }>(`/datahub/sources/${id}/delete-permanently/`),
  confirmSourceIdentity: (id: number, reason: string) =>
    api.post<DataHubSource>(`/datahub/sources/${id}/confirm_identity/`, { reason }),
  uploadSource: (body: {
    project_id: number;
    data_type: DataHubType;
    file: File;
    name?: string;
    sheet_name?: string;
    header_row?: number;
    unit_row?: number;
    data_start_row?: number;
    template_id?: string;
    max_header_scan_rows?: number;
  }) => {
    const form = new FormData();
    form.append('project_id', String(body.project_id));
    form.append('data_type', body.data_type);
    if (body.name) form.append('name', body.name);
    if (body.sheet_name) form.append('sheet_name', body.sheet_name);
    if (body.header_row) form.append('header_row', String(body.header_row));
    if (body.unit_row) form.append('unit_row', String(body.unit_row));
    if (body.data_start_row) form.append('data_start_row', String(body.data_start_row));
    if (body.template_id) form.append('template_id', body.template_id);
    if (body.max_header_scan_rows) form.append('max_header_scan_rows', String(body.max_header_scan_rows));
    form.append('file', body.file);
    return api.post<DataHubSource>('/datahub/sources/upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  processSource: (id: number, options: DataHubProcessOptions) =>
    api.post<{ run: unknown; dataset: DataHubDataset }>(`/datahub/sources/${id}/process/`, options, { timeout: 180000 }),
  reparseSource: (id: number, body: {
    data_type?: DataHubType;
    sheet_name?: string;
    header_row?: number;
    unit_row?: number;
    data_start_row?: number;
    template_id?: string;
    max_header_scan_rows?: number;
  }) =>
    api.post<DataHubSource>(`/datahub/sources/${id}/reparse/`, body, { timeout: 180000 }),
  listDatasets: (projectId: number, includeArchived = false) =>
    api.get<DataHubDataset[]>('/datahub/datasets/', { params: { project: projectId, include_archived: includeArchived ? 1 : undefined } }),
  getDataset: (id: number) =>
    api.get<DataHubDataset>(`/datahub/datasets/${id}/`),
  archiveDataset: (id: number) =>
    api.post<DataHubDataset>(`/datahub/datasets/${id}/archive/`),
  restoreDataset: (id: number) =>
    api.post<DataHubDataset>(`/datahub/datasets/${id}/restore/`),
  deleteDataset: (id: number) =>
    api.post<{ deleted: true; dataset_id: number }>(`/datahub/datasets/${id}/delete-permanently/`),
  getDatasetReadiness: (id: number) =>
    api.get<DataHubReadiness>(`/datahub/datasets/${id}/readiness/`),
  deriveGeomechanicsDataset: (id: number, body: GeomechanicsDatasetOperation) =>
    api.post<{ dataset: DataHubDataset; operation: Record<string, unknown> }>(
      `/datahub/datasets/${id}/derive-geomechanics/`,
      body,
      { timeout: 180000 },
    ),
  getRealWellAcceptance: (id: number) =>
    api.get<RealWellAcceptance>(`/datahub/datasets/${id}/real-well-acceptance/`),
  getDatasetRows: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get<{ id: number; row_count: number; columns: DataHubColumn[]; rows: Record<string, unknown>[] }>(
      `/datahub/datasets/${id}/data/`, { params }
    ),
  getDatasetAudit: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get<DataHubAuditResponse>(`/datahub/datasets/${id}/audit/`, { params }),
  applyDatasetCellDecisions: (id: number, body: {
    decision: 'accept' | 'raw' | 'edit' | 'exclude' | 'batch';
    cells: Array<{
      row_index: number;
      field: string;
      value?: unknown;
      decision?: 'accept' | 'raw' | 'edit' | 'exclude';
    }>;
  }) =>
    api.post<{
      updated_cells: number;
      decision: 'accept' | 'raw' | 'edit' | 'exclude' | 'batch';
      review_revision: number;
      content_checksum: string;
      changes: Array<{
        row_index: number;
        field: string;
        before: unknown;
        after: unknown;
        decision: 'accepted' | 'raw' | 'modified' | 'excluded';
      }>;
    }>(`/datahub/datasets/${id}/cell_decisions/`, body),
  getDatasetCurve: (id: number, params: { field: string; max_points?: number }) =>
    api.get<DataHubCurveResponse>(`/datahub/datasets/${id}/curve/`, { params }),
  applyDataset: (id: number, body: { options?: Record<string, unknown> } = {}) =>
    api.post<{ dataset: DataHubDataset; result: Record<string, any>; application?: Record<string, any>; idempotent?: boolean }>(
      `/datahub/datasets/${id}/apply/`, body, { timeout: 180000 },
    ),
  listApplications: (projectId: number) =>
    api.get<DataHubApplication[]>('/datahub/applications/', { params: { project: projectId } }),
  listIssues: (params: { project?: number; dataset?: number }) =>
    api.get<{ count: number; next: string | null; previous: string | null; results: DataHubIssue[] }>('/datahub/issues/', { params }),
};

// ── Geomechanics workbench ──
export type GeomechanicsModuleKey =
  | '00' | '01' | '02' | '03' | '04'
  | '05' | '06' | '07' | '08' | '09' | '10';

export type GeomechanicsCapabilities = {
  enabled: boolean;
  schema_version: string;
  engine_version: string;
  operations: string[];
  enabled_operations: string[];
  preview_operations: string[];
  limits: {
    max_rows: number;
    max_grid_cells: number;
    sync_timeout_seconds: number;
    max_artifact_bytes: number;
    max_inline_json_bytes: number;
    max_inline_array_items: number;
  };
};

export type GeomechanicsArtifactMetadata = {
  id: number;
  kind: 'input' | 'result' | 'export' | 'grid' | 'before_image';
  sha256: string;
  size_bytes: number;
  row_count: number;
  column_schema: Array<{ name: string; value_type: string }>;
  content_type: string;
  schema_version: string;
  created_at: string;
};

export type GeomechanicsRun = {
  id: number;
  project: number;
  operation: string;
  status:
    | 'draft'
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'reviewed'
    | 'published'
    | 'stale'
    | 'cancelling'
    | 'cancelled';
  schema_version: string;
  engine_version: string;
  depth_reference: 'MD' | 'TVD' | 'TVDSS';
  input_checksum: string;
  idempotency_key: string;
  parameters: Record<string, unknown>;
  summary: Record<string, unknown>;
  validation: Record<string, unknown>;
  calculation_description: Array<Record<string, unknown>>;
  error_code: string;
  error_message: string;
  review_decision: '' | 'approved' | 'conditional' | 'rejected';
  review_comment: string;
  revision: number;
  created_by: number | null;
  reviewed_by: number | null;
  input_dataset: number | null;
  parent_run: number | null;
  field_block: number | null;
  reviewed_at: string | null;
  published_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  attempt_count: number;
  created_at: string;
  updated_at: string;
  artifacts: GeomechanicsArtifactMetadata[];
  inputs: GeomechanicsRunInput[];
  dependencies: GeomechanicsRunDependency[];
};

export type GeomechanicsRunInput = {
  id: number;
  dataset: number;
  dataset_name: string;
  dataset_version: number;
  role: string;
  well_id: string;
  checksum: string;
  depth_reference: 'MD' | 'TVD' | 'TVDSS';
  created_at: string;
};

export type GeomechanicsRunDependency = {
  id: number;
  parent_run: number;
  parent_operation: string;
  parent_status: string;
  role: string;
  checksum: string;
  created_at: string;
};

export type GeomechanicsWorkbook = {
  fileName: string;
  sheetName: string;
  rows: Array<Record<string, unknown>>;
  columns: string[];
  numericColumns?: string[];
  depthColumn?: string;
  tvdColumn?: string;
};

export type GeomechanicsCalculationResult = {
  workbook: GeomechanicsWorkbook;
  descriptionRows: Array<Record<string, unknown>>;
};

export type GeomechanicsPreviewResponse = {
  result: GeomechanicsCalculationResult;
  lineage: Record<string, unknown>;
  validation: Record<string, unknown>;
};

export type GeomechanicsWorkspace = {
  id: number | null;
  project: number;
  user: number;
  revision: number;
  schema_version: string;
  active_module: GeomechanicsModuleKey;
  state: Record<string, unknown>;
  current_run: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type GeomechanicsSnapshot = {
  id: number;
  project: number;
  name: string;
  module_key: GeomechanicsModuleKey;
  schema_version: string;
  sha256: string;
  size_bytes: number;
  source_revision: number;
  created_by: number | null;
  base_run: number | null;
  created_at: string;
};

export type GeomechanicsFormulaPreset = {
  id: number;
  project: number;
  name: string;
  version: number;
  schema_version: string;
  payload: Record<string, unknown>;
  sha256: string;
  is_active: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

export type GeomechanicsPublicationPreview = {
  target: 'pressure' | 'rock_mechanics';
  source_run_id: number;
  depth_reference: 'MD' | 'TVD' | 'TVDSS';
  row_count: number;
  creates: Array<Record<string, unknown>>;
  updates: Array<Record<string, unknown>>;
  conflicts: Array<Record<string, unknown>>;
};

export type GeomechanicsPublication = {
  id: number;
  project: number;
  run: number;
  target: string;
  idempotency_key: string;
  status: 'pending' | 'succeeded' | 'failed' | 'rolled_back';
  created_count: number;
  updated_count: number;
  before_image: number | null;
  result_checksum: string;
  published_by: number | null;
  rolled_back_at: string | null;
  rolled_back_by: number | null;
  created_at: string;
  updated_at: string;
};

export type GeomechanicsDatasetOverview = {
  id: number;
  name: string;
  version: number;
  status: string;
  row_count: number;
  depth_reference: string;
  quality_score: number;
  content_checksum: string;
  source_id: number;
  source_archived: boolean;
  created_at: string;
  issue_count: number;
  units: Record<string, unknown>;
  depth_range: Array<unknown>;
};

export type GeomechanicsOverview = {
  project: number;
  datasets: Record<string, GeomechanicsDatasetOverview[]>;
  dataset_type_order: string[];
  runs: Record<
    string,
    {
      id: number;
      status: string;
      revision: number;
      row_count: number | null;
      result_checksum: string | null;
      input_checksum: string;
      depth_reference: string;
      reviewed: boolean;
      published: boolean;
      stale: boolean;
      error_code: string;
      created_at: string;
      inputs: Array<{
        dataset: number;
        role: string;
        well_id: string;
        checksum: string;
      }>;
      dependencies: Array<{
        parent_run: number;
        role: string;
        checksum: string;
      }>;
    }
  >;
  operation_order: string[];
  stable_tables: {
    pressure: {
      row_count: number;
      source_run_count: number;
      group_sources: Record<string, number>;
    };
    rock_mechanics: {
      row_count: number;
      engineering_source_count: number;
      dynamic_source_count: number;
      stress_source_count: number;
    };
  };
  status_summary: {
    stale: number;
    running: number;
    failed: number;
    total_runs: number;
  };
};

export type FieldBlockWell = {
  id: number;
  run: number | null;
  run_status: string | null;
  well_id: string;
  x: number;
  y: number;
  quality: number;
  zone: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
};

export type FieldBlock = {
  id: number;
  project: number;
  name: string;
  crs: string;
  x_unit: string;
  y_unit: string;
  description: string;
  revision: number;
  summary: Record<string, unknown>;
  created_by: number | null;
  well_count: number;
  wells: FieldBlockWell[];
  created_at: string;
  updated_at: string;
};

export type GeomechanicsDatasetOperation = {
  kind: 'curve' | 'fill';
  method: string;
  field?: string;
  target_field?: string;
  target_axis_field?: string;
  target_lookup_field?: string;
  source_dataset_id?: number;
  source_axis_field?: string;
  source_value_field?: string;
  source_density_field?: string;
  parameters?: Record<string, unknown>;
};

export type GeomechanicsInputProvenance = {
  output_field: string;
  dataset_field: string;
  standard_field: string;
  display_name: string;
  source_unit: string;
  target_unit: string;
  conversion: string;
  value_type: string;
  match_confidence: number | null;
  confirmation_required: boolean;
  derived: boolean;
  derivation?: string;
  origin: string;
};

export type GeomechanicsInputPreview = {
  dataset_id: number;
  dataset_version: number;
  source_id: number;
  project_id: number;
  content_checksum: string;
  depth_reference: 'MD' | 'TVD' | 'TVDSS' | '';
  row_count: number;
  returned_row_count: number;
  truncated: boolean;
  readiness: DataHubReadiness & { can_use: boolean };
  workbook: {
    fileName: string;
    sheetName: string;
    rows: Record<string, string | number | boolean | null>[];
    columns: string[];
    numericColumns: string[];
    depthColumn: string;
    tvdColumn?: string;
  };
  provenance: GeomechanicsInputProvenance[];
};

export type PostprocessSourceStatus =
  | 'missing-table'
  | 'empty-table'
  | 'missing-depth'
  | 'invalid-depth'
  | 'ready';

export type GeomechanicsPostprocessSource = {
  source_kind: 'dataset' | 'run';
  dataset_id: number;
  dataset_version: number;
  run_id: number | null;
  source_name: string;
  depth_column: string;
  row_count: number;
  valid_depth_count: number;
  min_depth: number | null;
  max_depth: number | null;
  content_checksum: string;
};

export type GeomechanicsPostprocessSourceResponse = {
  project: number;
  status: PostprocessSourceStatus;
  source: GeomechanicsPostprocessSource | null;
  message: string;
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const geomechanicsAPI = {
  getCapabilities: (signal?: AbortSignal) =>
    api.get<GeomechanicsCapabilities>('/geomechanics/capabilities/', { signal }),
  getOverview: (projectId: number, signal?: AbortSignal) =>
    api.get<GeomechanicsOverview>('/geomechanics/overview/', {
      params: { project: projectId },
      signal,
    }),
  getWorkspace: (projectId: number, signal?: AbortSignal) =>
    api.get<GeomechanicsWorkspace>('/geomechanics/workspace/', {
      params: { project: projectId },
      signal,
    }),
  getInputPreview: (datasetId: number, signal?: AbortSignal, limit = 500) =>
    api.get<GeomechanicsInputPreview>(`/geomechanics/inputs/${datasetId}/`, {
      params: { limit },
      signal,
    }),
  getPostprocessSource: (
    projectId: number,
    params: { datasetId?: number | null; runId?: number | null } = {},
    signal?: AbortSignal,
  ) => api.get<GeomechanicsPostprocessSourceResponse>('/geomechanics/postprocess/source/', {
    params: {
      project: projectId,
      ...(params.datasetId ? { dataset: params.datasetId } : {}),
      ...(params.runId ? { run: params.runId } : {}),
    },
    signal,
  }),
  previewCalculation: (body: {
    project_id: number;
    input_dataset_id?: number | null;
    parent_run_id?: number | null;
    operation: 'density-fit' | 'pore-trend' | 'rock-vsh' | 'rock-vsh-auto'
      | 'rock-segmentation' | 'rock-mechanics' | 'rock-calibration'
      | 'stress-magnitude' | 'fracture-pressure' | 'collapse-pressure'
      | 'safe-window' | 'block-model';
    depth_reference: 'MD' | 'TVD' | 'TVDSS';
    parameters: Record<string, unknown>;
  }, signal?: AbortSignal) =>
    api.post<GeomechanicsPreviewResponse>('/geomechanics/previews/', body, { signal }),
  createRun: (body: {
    project_id: number;
    input_dataset_id?: number | null;
    supporting_dataset_ids?: number[];
    parent_run_id?: number | null;
    operation: 'overburden' | 'formation-pressure' | 'rock-segmentation'
      | 'rock-mechanics' | 'rock-calibration' | 'stress-magnitude'
      | 'fracture-pressure' | 'collapse-pressure' | 'safe-window'
      | 'block-model';
    field_block_id?: number | null;
    depth_reference: 'MD' | 'TVD' | 'TVDSS';
    parameters: Record<string, unknown>;
  }) => api.post<GeomechanicsRun>('/geomechanics/runs/', body),
  listRuns: (
    projectId: number,
    params?: { operation?: string; status?: string },
    signal?: AbortSignal,
  ) =>
    api.get<PaginatedResponse<GeomechanicsRun>>('/geomechanics/runs/', {
      params: { project: projectId, ...params },
      signal,
    }),
  getRun: (runId: number, signal?: AbortSignal) =>
    api.get<GeomechanicsRun>(`/geomechanics/runs/${runId}/`, { signal }),
  calculateRun: async (runId: number) => {
    const response = await api.post<{
      run: GeomechanicsRun;
      result?: GeomechanicsCalculationResult;
      idempotent?: boolean;
      async?: boolean;
      task_id?: string;
    }>(`/geomechanics/runs/${runId}/calculate/`, {});
    const data = response.data;
    if (data.result || !data.async) {
      return {
        data: {
          run: data.run,
          result: data.result as GeomechanicsCalculationResult,
          idempotent: Boolean(data.idempotent),
        },
        status: response.status,
      };
    }
    // Celery 模式：轮询 Run 直到 succeeded/failed/cancelled，再取结果 artifact。
    const deadline = Date.now() + 120_000;
    for (;;) {
      const run = (await api.get<GeomechanicsRun>(`/geomechanics/runs/${runId}/`)).data;
      if (run.status === 'succeeded' || run.status === 'reviewed') {
        const artifact = await api.get<{
          artifact: GeomechanicsArtifactMetadata;
          payload: GeomechanicsCalculationResult | Record<string, unknown>;
        }>(`/geomechanics/runs/${runId}/artifacts/result/`);
        return {
          data: {
            run,
            result: artifact.data.payload as GeomechanicsCalculationResult,
            idempotent: false,
          },
          status: 200,
        };
      }
      if (run.status === 'failed' || run.status === 'cancelled' || run.status === 'stale') {
        throw new Error(`计算未成功（${run.status}）：${run.error_message || run.error_code || ''}`);
      }
      if (Date.now() > deadline) {
        throw new Error('地质力学计算等待超时（120s）。');
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  },
  cancelRun: (runId: number) =>
    api.post<GeomechanicsRun>(`/geomechanics/runs/${runId}/cancel/`, {}),
  retryRun: (runId: number) =>
    api.post<GeomechanicsRun>(`/geomechanics/runs/${runId}/retry/`, {}),
  getRunArtifact: (runId: number, kind: 'input' | 'result', signal?: AbortSignal) =>
    api.get<{
      artifact: GeomechanicsArtifactMetadata;
      payload: GeomechanicsCalculationResult | Record<string, unknown>;
    }>(`/geomechanics/runs/${runId}/artifacts/${kind}/`, { signal }),
  getArtifactRows: (
    runId: number,
    kind: 'input' | 'result' | 'grid',
    params: { limit?: number; offset?: number },
    signal?: AbortSignal,
  ) =>
    api.get<{
      artifact: GeomechanicsArtifactMetadata;
      columns: string[];
      rows: Array<Record<string, unknown>>;
      returned_row_count: number;
      total_row_count: number;
      offset: number;
      limit: number;
    }>(`/geomechanics/runs/${runId}/artifacts/${kind}/rows/`, {
      params,
      signal,
    }),
  reviewRun: (runId: number, body: {
    revision: number;
    decision: 'approved' | 'conditional' | 'rejected';
    comment?: string;
  }) => api.post<GeomechanicsRun>(`/geomechanics/runs/${runId}/review/`, body),
  publishPreview: (runId: number) =>
    api.post<GeomechanicsPublicationPreview>(
      `/geomechanics/runs/${runId}/publish-preview/`,
      {},
    ),
  publishRun: (runId: number) =>
    api.post<GeomechanicsPublication>(
      `/geomechanics/runs/${runId}/publish/`,
      {},
    ),
  listPublications: (projectId: number, signal?: AbortSignal) =>
    api.get<PaginatedResponse<GeomechanicsPublication>>(
      '/geomechanics/publications/',
      { params: { project: projectId }, signal },
    ),
  rollbackPublication: (publicationId: number) =>
    api.post<GeomechanicsPublication>(
      `/geomechanics/publications/${publicationId}/rollback/`,
      {},
    ),
  saveWorkspace: (body: {
    project_id: number;
    revision: number;
    schema_version: string;
    active_module: GeomechanicsModuleKey;
    state: Record<string, unknown>;
    current_run_id?: number | null;
  }) => api.put<GeomechanicsWorkspace>('/geomechanics/workspace/', body),
  listSnapshots: (projectId: number, signal?: AbortSignal) =>
    api.get<PaginatedResponse<GeomechanicsSnapshot>>('/geomechanics/snapshots/', {
      params: { project: projectId },
      signal,
    }),
  getSnapshot: (snapshotId: number, signal?: AbortSignal) =>
    api.get<GeomechanicsSnapshot>(`/geomechanics/snapshots/${snapshotId}/`, { signal }),
  createSnapshot: (body: {
    project_id: number;
    revision: number;
    name: string;
    module_key: GeomechanicsModuleKey;
  }) => api.post<GeomechanicsSnapshot>('/geomechanics/snapshots/', body),
  restoreSnapshot: (snapshotId: number, revision: number) =>
    api.post<GeomechanicsWorkspace>(
      `/geomechanics/snapshots/${snapshotId}/restore/`,
      { revision },
    ),
  listFormulaPresets: (projectId: number, signal?: AbortSignal) =>
    api.get<PaginatedResponse<GeomechanicsFormulaPreset>>(
      '/geomechanics/formula-presets/',
      { params: { project: projectId }, signal },
    ),
  createFormulaPreset: (body: {
    project_id: number;
    name: string;
    schema_version?: string;
    payload: Record<string, unknown>;
    is_active?: boolean;
  }) => api.post<GeomechanicsFormulaPreset>('/geomechanics/formula-presets/', body),
  activateFormulaPreset: (presetId: number, version: number) =>
    api.post<GeomechanicsFormulaPreset>(
      `/geomechanics/formula-presets/${presetId}/activate/`,
      { version },
    ),
  listFieldBlocks: (projectId: number, signal?: AbortSignal) =>
    api.get<PaginatedResponse<FieldBlock>>('/geomechanics/field-blocks/', {
      params: { project: projectId },
      signal,
    }),
  createFieldBlock: (body: {
    project_id: number;
    name: string;
    crs?: string;
    x_unit?: string;
    y_unit?: string;
    description?: string;
  }) => api.post<FieldBlock>('/geomechanics/field-blocks/', body),
  getFieldBlock: (blockId: number, signal?: AbortSignal) =>
    api.get<FieldBlock>(`/geomechanics/field-blocks/${blockId}/`, { signal }),
  updateFieldBlock: (blockId: number, body: {
    revision: number;
    name: string;
    crs: string;
    x_unit: string;
    y_unit: string;
    description?: string;
  }) => api.put<FieldBlock>(`/geomechanics/field-blocks/${blockId}/`, body),
  listFieldBlockWells: (blockId: number, signal?: AbortSignal) =>
    api.get<FieldBlockWell[]>(
      `/geomechanics/field-blocks/${blockId}/wells/`,
      { signal },
    ),
  addFieldBlockWell: (blockId: number, body: {
    run_id: number;
    well_id: string;
    x: number;
    y: number;
    quality?: number;
    zone?: string;
    is_primary?: boolean;
  }) => api.post<FieldBlockWell>(
    `/geomechanics/field-blocks/${blockId}/wells/`,
    body,
  ),
  removeFieldBlockWell: (blockId: number, wellId: string) =>
    api.delete(`/geomechanics/field-blocks/${blockId}/wells/${wellId}/`),
};

// ── Rock Mechanics ──
export const mechanicsAPI = {
  getProfile: (projectId: number) =>
    api.get('/mechanics/', { params: { project: projectId } }),
  batchImport: (data: { project_id: number; points: unknown[] }) =>
    api.post('/mechanics/batch_import/', data),
};

// ── Fracture Design ──
/** ai_design 的留痕: 甲方问"这个数哪来的"时, 答案就在这里。 */
export type AiDesignAudit = {
  source: 'llm' | 'rule_based';
  model?: string | null;
  rationale?: string;
  risks?: string[];
  clamped?: { field: string; requested: unknown; applied: unknown; reason: string }[];
  error?: string;
  error_code?: string;
  error_hint?: string;
  context_used?: boolean;
};

export type PublishedNaturalFractureModel = {
  modelId: number;
  name: string;
  version: number;
  projectId: number;
  wellId?: number | string | null;
  sourceDatasetId: number;
  status: 'PUBLISHED';
  publishedAt?: string | null;
  confidence?: number | null;
  coordinateReference: string;
  depthReference: string;
  minMd?: number | null;
  maxMd?: number | null;
  fractureCount: number;
  fractureSets: unknown[];
  baselineParameters?: {
    orientationStrikeDeg?: number | null;
    dipDeg?: number | null;
    intensity?: number | null;
    frictionCoefficient?: number | null;
    cohesionMpa?: number | null;
  };
  parameterAvailability?: Record<string, boolean>;
  parameterProvenance?: Record<string, string | null>;
  fractures?: Array<Record<string, unknown>>;
  validation?: { status: 'VALID' | 'REVIEW' | 'BLOCKED'; issues: Array<{ code: string; level: string; message: string }> };
};

export type FractureDesignConstraint = {
  value: number | boolean;
  unit: string;
  source: 'DEFAULT' | 'ENGINEERING_DEFAULT' | 'PROJECT' | 'USER' | 'GEOLOGY_DERIVED';
};

export type GeologyCandidateDesign = {
  candidateId: string;
  source: string;
  baseDesignId: number;
  status: 'FEASIBLE' | 'FEASIBLE_WITH_WARNINGS' | 'INFEASIBLE';
  constraintCount: number;
  constraintViolations: Array<Record<string, unknown>>;
  warnings: Array<Record<string, unknown>>;
  scenarioParameters?: Record<string, number>;
  provenance?: Record<string, unknown>;
  stages: Array<{
    stageNumber: number;
    topMd: number;
    bottomMd: number;
    clusterSpacingM?: number;
    clusterPositionsMd: number[];
    geologySummary: Record<string, unknown>;
    designReason?: string[];
  }>;
};

export type FractureDesignBasisResponse = {
  workflowVersion: string;
  designMode: 'MANUAL' | 'GEOLOGY_CONSTRAINED';
  requestedDesignMode: 'MANUAL' | 'GEOLOGY_CONSTRAINED';
  manualFallback: boolean;
  fallbackReason?: string | null;
  baseDesignId: number;
  baseDesignVersion: string;
  geologyContext: Record<string, any>;
  constraints: Record<string, FractureDesignConstraint>;
  candidateDesigns: GeologyCandidateDesign[];
  postFracCapability: Record<string, any>;
  fieldCalibration: Record<string, any>;
  architectureBoundary: Record<string, unknown>;
};

export type CanonicalNaturalFracture = {
  id: string;
  centerXYZ: [number, number, number];
  dipDirectionDeg: number;
  dipDeg: number;
  strikeDeg: number;
  majorLengthM: number;
  minorLengthM: number;
  apertureM?: number | null;
  source: 'PUBLISHED_GEOLOGICAL' | 'STOCHASTIC' | 'HYBRID_STOCHASTIC' | 'GEOLOGY' | 'CUSTOM';
  sourceDatasetId?: number | null;
  sourceModelId?: number | null;
  sourceVersion?: number | null;
  formationId?: number | string | null;
  layerId?: number | string | null;
  fractureSetId?: string | null;
  provenance: Record<string, unknown>;
};

export type StageLocalNaturalFractureModel = {
  modelVersion: 'STAGE_LOCAL_NF_V1';
  stageId: string;
  candidateCount: number;
  selectedCount: number;
  fractures: CanonicalNaturalFracture[];
  generationSource: 'GEOLOGICAL' | 'STOCHASTIC' | 'HYBRID' | 'GEOLOGY' | 'GEOLOGY_PLUS_CUSTOM' | 'CUSTOM';
  influenceDomain: {
    bounds: { minXYZ: [number, number, number]; maxXYZ: [number, number, number] };
    p3dPredictedHalfLengthM?: number | null;
    p3dPredictedUpperGrowthM?: number | null;
    p3dPredictedLowerGrowthM?: number | null;
    nfSearchMarginFactor: number;
    lateralSearchExtentM: number;
    upperSearchExtentM: number;
    lowerSearchExtentM: number;
  };
  validation: { status: 'VALID' | 'VALID_WITH_WARNING' | 'INVALID'; issues: Array<Record<string, unknown>> };
  statistics: Record<string, unknown>;
  capabilities: Record<string, { status: 'AVAILABLE' | 'PARTIAL' | 'NOT_AVAILABLE'; reason?: string | null }>;
};

export type StageLocalNaturalFracturePreview = {
  status: 'VALID' | 'VALID_WITH_WARNING' | 'INVALID' | 'SPATIAL_LOCATION_UNAVAILABLE';
  modelVersion: 'STAGE_LOCAL_NF_V1';
  sourceMode: 'GEOLOGICAL' | 'STOCHASTIC' | 'HYBRID' | 'GEOLOGY' | 'GEOLOGY_PLUS_CUSTOM' | 'CUSTOM';
  globalFractureCount: number;
  wellCandidatePool: { globalCount: number; candidateCount: number; corridor: Record<string, unknown> };
  models: StageLocalNaturalFractureModel[];
  capabilities: StageLocalNaturalFractureModel['capabilities'];
  architectureBoundary: { implemented: string; notImplemented: string };
};

export type SimulationJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type SimulationJob = {
  jobId: string;
  simulationId: number;
  projectId: number;
  designId: number;
  status: SimulationJobStatus;
  progress: number;
  currentTime: number;
  totalTime: number;
  currentStep: number;
  totalSteps: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  failureReason: string | null;
  lastConvergedFrame?: Record<string, unknown> | null;
  diagnostics?: Record<string, unknown>;
  metrics?: Record<string, unknown>;
};

export type SimulationSseEvent = {
  id: number;
  type: string;
  data: Record<string, any>;
};

const streamSimulationEvents = async (
  jobId: string,
  onEvent: (event: SimulationSseEvent) => void,
  options: { lastEventId?: number; signal?: AbortSignal } = {},
): Promise<void> => {
  const token = localStorage.getItem('access_token');
  const query = options.lastEventId ? `?lastEventId=${options.lastEventId}` : '';
  const response = await fetch(`${API_BASE}/fracture/simulations/${jobId}/events/${query}`, {
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'same-origin',
    signal: options.signal,
  });
  if (!response.ok || !response.body) throw new Error(`SSE connection failed (${response.status})`);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    blocks.forEach((block) => {
      if (!block || block.startsWith(':')) return;
      let id = 0;
      let type = 'message';
      const dataLines: string[] = [];
      block.split('\n').forEach((line) => {
        if (line.startsWith('id:')) id = Number(line.slice(3).trim()) || 0;
        else if (line.startsWith('event:')) type = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
      });
      if (dataLines.length) onEvent({ id, type, data: JSON.parse(dataLines.join('\n')) });
    });
    if (done) return;
  }
};

export const fractureAPI = {
  listDesigns: (projectId: number) =>
    api.get('/fracture/designs/', { params: { project: projectId } }),
  createDesign: (data: Record<string, unknown>) =>
    api.post('/fracture/designs/', data),
  getDesign: (id: number) => api.get(`/fracture/designs/${id}/`),
  autoDesign: (data: { project_id: number; options?: Record<string, unknown> }) =>
    api.post('/fracture/designs/auto_design/', data),
  // AI 辅助设计: 大模型定取向与参数区间, 逐段泵注程序仍由后端模板引擎算。
  // 返回体比 autoDesign 多一个 ai: {source, model, rationale, risks, clamped[]}。
  aiDesign: (data: { project_id: number; brief?: string; options?: Record<string, unknown> }) =>
    api.post<{ id: number; name: string; ai: AiDesignAudit }>('/fracture/designs/ai_design/', data),
listAnalogCandidates: (projectId: number) =>
    api.get('/fracture/designs/analog_candidates/', { params: { project: projectId } }),
  createAnalogDesign: (data: { project_id: number; source_design_id: number; allow_unvalidated?: boolean }) =>
    api.post('/fracture/designs/analog_design/', data),
  runSampleCase: (projectId: number) =>
    api.post('/fracture/designs/run_sample_case/', { project_id: projectId }),
  getFormationInputs: (projectId: number, params?: { step_m?: number; include_samples?: boolean }) =>
    api.get('/fracture/designs/formation_inputs/', { params: { project: projectId, ...params } }),
  getIntelligenceFramework: (projectId: number) =>
    api.get('/fracture/designs/intelligence_framework/', { params: { project: projectId } }),
  estimateLeakoff: (projectId: number, params?: { reservoir_type?: string; mu_pa_s?: number; pn_mpa?: number }) =>
    api.get('/fracture/designs/estimate_leakoff/', { params: { project: projectId, ...params } }),
  importPlan: (data: { project_id: number; design: Record<string, unknown>; stages: unknown[] }) =>
    api.post('/fracture/designs/import_plan/', data),
  deleteDesign: (id: number) => api.delete(`/fracture/designs/${id}/`),
  // Word 版方案报告。responseType 必须是 blob —— 默认会被 axios 当文本处理, 存下来的 docx 打不开。
  exportDesignDocx: (id: number) =>
    api.get(`/fracture/designs/${id}/export_docx/`, { responseType: 'blob', timeout: 120000 }),
bulkDeleteDesigns: (ids: number[]) => api.post('/fracture/designs/bulk_delete/', { ids }),
  activateDesign: (id: number) => api.post(`/fracture/designs/${id}/activate/`),
  renameDesign: (id: number, name: string) =>
    api.post(`/fracture/designs/${id}/rename/`, { name }),
  cloneDesign: (id: number) => api.post(`/fracture/designs/${id}/clone/`),
  patchDesign: (id: number, body: Record<string, unknown>) =>
    api.patch(`/fracture/designs/${id}/`, body),
  clearStageCluster: (id: number) =>
    api.post(`/fracture/designs/${id}/clear_stage_cluster/`),
  saveSimulationHandoff: (data: {
    project_id?: number;
    design_id?: number;
    completion?: Record<string, unknown>;
    materials?: Record<string, unknown>;
    pump_programs?: Array<Record<string, unknown>>;
  }) => api.post('/fracture/designs/simulation_handoff/', data),
  // 压裂模拟
  simulate: (data: { design_id: number; options?: Record<string, unknown> }) =>
    api.post('/fracture/simulations/simulate/', data),
  startAsyncSimulation: (data: { design_id: number; options?: Record<string, unknown> }) =>
    api.post<SimulationJob>('/fracture/simulations/async/', data),
  getSimulationJob: (jobId: string) =>
    api.get<SimulationJob>(`/fracture/simulations/${jobId}/job/`),
  cancelSimulationJob: (jobId: string) =>
    api.post<SimulationJob>(`/fracture/simulations/${jobId}/cancel/`),
  streamSimulationEvents,
  getSimulationReadiness: (designId: number, options?: Record<string, unknown>) =>
    api.post<P3DSimulationReadiness>('/fracture/simulations/readiness/', { design_id: designId, options }),
  listSimulations: (params: { project?: number; design?: number }) =>
    api.get('/fracture/simulations/', { params }),
  getSimulation: (simulationId: number) =>
    api.get(`/fracture/simulations/${simulationId}/`),
  listPublishedNaturalFractureModels: (projectId: number) =>
    api.get<{ results: PublishedNaturalFractureModel[] }>('/fracture/simulations/natural-fracture-models/', { params: { project: projectId } }),
  getPublishedNaturalFractureModel: (projectId: number, modelId: number, designId?: number, includeFractures = true) =>
    api.get<PublishedNaturalFractureModel>('/fracture/simulations/natural-fracture-models/', {
      params: { project: projectId, model: modelId, design: designId, include_fractures: includeFractures },
    }),
  previewStageLocalNaturalFractures: (data: Record<string, unknown>) =>
    api.post<StageLocalNaturalFracturePreview>('/fracture/simulations/stage-local-natural-fractures/preview/', data),
  sensitivity: (data: Record<string, unknown>) =>
    api.post('/fracture/simulations/sensitivity/', data),
  scenarioSensitivity: (data: Record<string, unknown>) =>
    api.post('/fracture/simulations/scenario-sensitivity/', data),
  parameterSweep: (data: Record<string, unknown>) =>
    api.post('/fracture/simulations/parameter-sweep/', data),
  getDesignBasis: (data: Record<string, unknown>) =>
    api.post<FractureDesignBasisResponse>('/fracture/simulations/design-basis/', data),
  applyDesignVariant: (data: Record<string, unknown>) =>
    api.post('/fracture/simulations/apply-design-variant/', data),
  exportCsv: (simId: number) =>
    api.get(`/fracture/simulations/${simId}/csv_export/`, { responseType: 'blob' }),
  listStages: (designId: number) =>
    api.get('/fracture/stages/', { params: { design: designId } }),
  generatePumpProgram: (stageId: number, data: { generate?: boolean; parameters?: Record<string, unknown>; locked_steps?: unknown[]; ai_intent?: Record<string, unknown> }) =>
    api.post(`/fracture/stages/${stageId}/generate_program/`, data),
  confirmPumpParameters: (stageId: number, parameters: Record<string, unknown>) =>
    api.post(`/fracture/stages/${stageId}/confirm_parameters/`, { parameters }),
  savePumpProgram: (stageId: number, data: { steps: unknown[]; parameters?: Record<string, unknown> }) =>
    api.post(`/fracture/stages/${stageId}/save_program/`, data),
  scorePumpProgram: (stageId: number, steps: unknown[]) =>
    api.post(`/fracture/stages/${stageId}/score_program/`, { steps }),
  listStepFrictionDatasets: (designId: number, stageId: number | string) =>
    api.get<StepFrictionDataset[]>('/fracture/pressure-calibration/step-friction/datasets/', {
      params: { design_id: designId, stage_id: stageId },
    }),
  analyzeStepFriction: (data: Record<string, unknown>) =>
    api.post('/fracture/pressure-calibration/step-friction/analyze/', data),
  publishStepFriction: (data: Record<string, unknown>) =>
    api.post('/fracture/pressure-calibration/step-friction/publish/', data),
};

export type StepFrictionDataset = {
  id: number; name: string; version: number; well: string; stage: number | string;
  sourceFile: string; pressureReference: string; timeSource: string;
  qcStatus: 'PASSED' | 'LOW'; qualityScore: number; publishedAt?: string | null;
};

export type P3DSimulationReadinessItem = {
  code: string;
  label?: string;
  status?: string;
  level?: 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL_CALIBRATION' | 'OPTIONAL_ANALYSIS';
};

export type P3DSimulationReadiness = {
  simulation_ready: boolean;
  simulationReady: boolean;
  missing_required: P3DSimulationReadinessItem[];
  missingRequired: P3DSimulationReadinessItem[];
  missing_recommended: P3DSimulationReadinessItem[];
  missingRecommended: P3DSimulationReadinessItem[];
  available_calibration: P3DSimulationReadinessItem[];
  availableCalibration: P3DSimulationReadinessItem[];
  missing_calibration: P3DSimulationReadinessItem[];
  missingCalibration: P3DSimulationReadinessItem[];
  fallbacks: P3DSimulationReadinessItem[];
};

// ── Warning ──
export type WarnScenario = 'normal' | 'screenout' | 'frac_hit';
export type MonitorSample = {
  t_s: number;
  surface_pressure_mpa: number;
  bottomhole_pressure_mpa: number;
  net_pressure_mpa: number;
  rate_m3min: number;
  proppant_conc_kgm3: number;
  casing_pressure_mpa: number;
};
export type WarnEvent = {
  id: number; t_s: number; level: string; mode: string;
  slope: number; message: string; suggestion: string; acknowledged: boolean;
};
export type MonitorRun = {
  id: number; design: number | null; design_name: string;
  well_name?: string;
  stage_number: number; scenario: WarnScenario | 'real';
  source?: 'synthetic' | 'imported';
  duration_s: number; dt_s: number;
  peak_pressure_mpa: number; avg_rate_m3min: number; max_conc_kgm3: number;
  total_fluid_m3?: number; total_proppant_t?: number;
  verdict: 'safe' | 'warn' | 'alarm';
  sample_count: number; event_count: number;
  created_at: string;
};
export type MonitorRunData = {
  run: MonitorRun;
  samples: MonitorSample[];
  analysis: {
    t: number[]; slope: number[]; mode: string[];
    log_t: number[]; log_pn: number[];
    events: { t_s: number; slope: number; mode: string; level: string; message: string; suggestion: string }[];
  };
  events: WarnEvent[];
};
export type WarnRule = {
  id: number; name: string; parameter: string;
  threshold_min: number | null; threshold_max: number | null;
  level: string; is_active: boolean; action_suggestion: string;
};
export const warningAPI = {
  getRules: (projectId: number) =>
    api.get<WarnRule[]>('/warning/rules/', { params: { project: projectId } }),
  createRule: (body: Partial<WarnRule> & { project: number }) =>
    api.post('/warning/rules/', body),
  updateRule: (id: number, body: Partial<WarnRule>) =>
    api.patch(`/warning/rules/${id}/`, body),
  deleteRule: (id: number) =>
    api.delete(`/warning/rules/${id}/`),
  getEvents: (projectId: number) =>
    api.get('/warning/events/', { params: { project: projectId } }),
  acknowledge: (eventId: number) =>
    api.post(`/warning/events/${eventId}/acknowledge/`),
  listRuns: (projectId: number) =>
    api.get<MonitorRun[]>('/warning/runs/', { params: { project: projectId } }),
  getRunData: (id: number) =>
    api.get<MonitorRunData>(`/warning/runs/${id}/data/`),
  generateRun: (body: { design_id: number; stage_number: number; scenario: WarnScenario; duration_min?: number }) =>
    api.post<{ id: number; verdict: string; sample_count: number; event_count: number }>(
      '/warning/runs/generate/', body, { timeout: 60000 }
    ),
  deleteRun: (id: number) =>
    api.delete(`/warning/runs/${id}/`),
  importCurve: (form: FormData) =>
    api.post<{ id: number; well_name: string; stage_number: number; sample_count: number; duration_s: number; peak_pressure_mpa: number; total_fluid_m3: number; total_proppant_t: number }>(
      '/warning/runs/import_curve/', form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    ),
  trainModel: () =>
    api.post<{ trained_samples: number; runs: number }>('/warning/runs/train_model/', {}, { timeout: 300000 }),
  predictRun: (id: number) =>
    api.get<{ horizon_s: number; t: number[]; pred: number[]; actual: number[] }>(`/warning/runs/${id}/predict/`),
};

// ── Model3D ──
export const model3dAPI = {
  list: (projectId: number) =>
    api.get('/model3d/', { params: { project: projectId } }),
  upload: (formData: FormData) =>
    api.post('/model3d/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ── AI 辅助 ──
export type AIMessage = { role: 'user' | 'assistant' | 'system'; content: string };
/** AI 参考文档 —— 不限格式上传, 抽到文本的才能喂给模型 (usable)。
 *  和数据中心是两条路: 这里不解析成表、不进计算链。 */
export type AIDocument = {
  id: number;
  project: number;
  name: string;
  original_filename: string;
  size_bytes: number;
  extract_status: 'pending' | 'ok' | 'unsupported' | 'empty' | 'failed';
  extract_status_display: string;
  extract_error: string;
  char_count: number;
  page_count: number;
  usable: boolean;
  preview: string;
  uploaded_by_name?: string;
  created_at: string;
};

export type AIContextStatus = {
  project_id: number | null;
  has_data: boolean;
  counts: { key: string; label: string; unit: string; count: number }[];
  notice: string;
};


export type CompletionDesignStage = {
  stageNumber: number; topMd: number; bottomMd: number; topTvd: number; bottomTvd: number;
  completion: { mode: 'cased-perforation' | 'cemented-sleeve' | 'openhole-packer-sleeve' | 'openhole'; casingType: string; casingInnerDiameter: number; isolationMethod: string; pressureRating: number };
  clusters: Array<{ topMd: number; bottomMd: number; effectiveHoles: number }>;
  sweetGrade: string; sweetScore: number; formation: string; lithology: string;
  basis: string[]; confidence: number;
};
export type CompletionDesignResponse = {
  parameters: { holeDiameter: number; erosionCoefficient: number; initialFlowCoefficient: number; maximumFlowCoefficient: number };
  stages: CompletionDesignStage[];
  evidence: { sweet_spot_intervals: number; formation_layers: number; survey_points: number; mechanics_points: number; pressure_points: number; collapse_zones: number; casing_sections: number; historical_case_ids: number[] };
  warnings: string[];
  summary: { stage_count: number; mode_counts: Record<string, number>; source: string; requires_engineering_review: boolean };
  ai: { source: 'llm' | 'rule_based'; model?: string | null; rationale?: string; risks?: string[]; error?: string; error_hint?: string; applied_intent?: { completion_bias: string; hole_bias: number } };
};
export const aiAPI = {
  chat: (data: {
    messages: AIMessage[];
    project_id?: number | null;
    temperature?: number;
    document_ids?: number[];
    language?: import('../i18n').Lang;
  }) =>
    api.post<{ reply: string; usage?: Record<string, number>; model?: string }>(
      '/ai/chat/', data, { timeout: 180000 }
    ),
  info: () =>
    api.get<{ model?: string; json_model?: string; provider?: string; configured: boolean }>('/ai/info/'),

  /** 当前项目有多少数据真正进了模型的上下文。
   *  和聊天用的是同一处判定 —— 这条提示不能变成第二个会说谎的地方。 */
  contextStatus: (projectId?: number | null) =>
    api.get<AIContextStatus>('/ai/context-status/',
      projectId ? { params: { project: projectId } } : undefined),

  listDocuments: (projectId: number) =>
    api.get<AIDocument[]>('/ai/documents/', { params: { project: projectId } }),
  uploadDocument: (projectId: number, file: File, note?: string) => {
    const form = new FormData();
    form.append('project', String(projectId));
    form.append('file', file);
    if (note) form.append('note', note);
    return api.post<AIDocument>('/ai/documents/', form, { timeout: 180000 });
  },
  deleteDocument: (id: number) => api.delete(`/ai/documents/${id}/`),
  documentText: (id: number) =>
    api.get<{ id: number; name: string; extract_status: string; char_count: number; text: string }>(
      `/ai/documents/${id}/text/`
    ),
  completionDesign: (data: { project_id: number; stages?: Array<{ stage_number: number; top_md: number; bottom_md: number; cluster_count?: number }> }) =>
    api.post<CompletionDesignResponse>('/ai/completion-design/', data, { timeout: 180000 }),
  stagingDesignIntent: (data: { project_id: number; design_id?: number }) =>
    api.post<{ intent: { reservoirType: string; designIntent: string; minStageLength: number; maxStageLength: number; clusterCount: number; clusterSpacing: number }; ai: { source: 'llm' | 'rule_based'; model?: string | null; rationale?: string; risks?: string[]; error?: string }; case_ids: number[] }>('/ai/staging-design-intent/', data, { timeout: 180000 }),
  pumpDesignIntent: (data: { project_id: number; design_id?: number }) =>
    api.post<{ intent: { design_intent: string; pad_ratio: number; flush_ratio: number; concentration_stage_bias: number; rate_factor: number }; ai: { source: 'llm' | 'rule_based'; model?: string | null; rationale?: string; risks?: string[]; error?: string }; case_ids: number[] }>('/ai/pump-design-intent/', data, { timeout: 180000 }),
  explainCompletion: (data: { project_id: number; question: string; messages?: AIMessage[]; completion_context?: import('./completionAIContext').CompletionAIContext; language?: import('../i18n').Lang }) =>
    api.post<{ reply: string; engine: string }>('/ai/completion-design/explain/', data, { timeout: 60000 }),
  explainDesign: (data: { project_id: number; question: string; design_id?: number; neighbor_design_id?: number; messages?: AIMessage[]; engineering_context?: import('./engineeringAIContext').EngineeringAIContext; language?: import('../i18n').Lang }) =>
    api.post<{ reply: string; engine: string; design_id: number }>('/ai/design/explain/', data, { timeout: 60000 }),

  // 操作模式: 自然语言 → 命令目录里的步骤 (只规划, 执行在前端)
  agentPlan: (data: {
    message: string;
    catalog: unknown[];
    history?: AIMessage[];
    page?: string;
    page_state?: string;
    project_id?: number | null;
    language?: import('../i18n').Lang;
  }) =>
    api.post<{ reply: string; actions: Array<{ command: string; args: Record<string, unknown>; reason?: string }>; rejected: string[] }>(
      '/ai/agent/plan/', data, { timeout: 120000 }
    ),

  // 历史对话: 不带 project 时只列未绑定项目的对话
  listConversations: (projectId?: number | null) =>
    api.get<AIConversation[]>('/ai/conversations/', projectId ? { params: { project: projectId } } : undefined),
  createConversation: (data: { project: number | null; page: string }) =>
    api.post<AIConversation>('/ai/conversations/', data),
  getConversation: (id: number) =>
    api.get<AIConversation & { messages: Array<AIMessage & { id: number; created_at: string }> }>(`/ai/conversations/${id}/`),
  appendConversationMessages: (id: number, messages: AIMessage[]) =>
    api.post<{ id: number; title: string; added: number }>(`/ai/conversations/${id}/messages/`, { messages }),
  renameConversation: (id: number, title: string) =>
    api.patch<AIConversation>(`/ai/conversations/${id}/`, { title }),
  deleteConversation: (id: number) => api.delete(`/ai/conversations/${id}/`),
};

export type AIConversation = {
  id: number;
  project: number | null;
  page: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
};

// ── 自适应调控 (强化学习) ──
export type ControlTrace = {
  t: number[]; rate: number[]; conc: number[]; pn: number[];
  L: number[]; propped: number[]; so_ratio: number[];
};
export type ControlSummary = {
  propped_half_length: number; half_length: number; proppant_kg: number;
  fluid_m3: number; efficiency: number; conductivity: number;
  screenout: boolean; duration_min: number;
};
export type ControlSide = { trace: ControlTrace; summary: ControlSummary; outline: number[][] };
export type ControlRunResp = {
  seed: number;
  scenario: Record<string, number>;
  rl: ControlSide;
  baseline: ControlSide;
  uplift: { propped_half_length: number; proppant_kg: number; conductivity: number };
};
export type ControlMetrics = {
  n: number; seed: number;
  results: Record<string, {
    propped_half_length: number; proppant_kg: number; conductivity: number;
    efficiency: number; screenout_rate: number; n: number;
  }>;
};
export type ControlHistoryPoint = {
  iter: number; reward: number; screenout: number; propped_len: number; proppant: number;
};
export const controlAPI = {
  metrics: () => api.get<ControlMetrics>('/control/metrics/'),
  history: () => api.get<ControlHistoryPoint[]>('/control/history/'),
  run: (body: { seed?: number; fluid_budget?: number; q_max_total?: number; conc_max?: number; CL?: number }) =>
    api.post<ControlRunResp>('/control/run/', body, { timeout: 60000 }),
};

// ── 裂缝反演 (微地震) ──
export type InvFracture = {
  polygon: number[][]; strike_deg: number; length_m: number;
  height_m: number; n_points: number; fit_rms_m: number;
};
/** 凹包改造面积。凸包 sra_1e4m2 会把缝间空白算进去, 是上界; 这个贴合事件分布。 */
export type InvSraAlpha = {
  sra_alpha_1e4m2: number; alpha_m: number | null;
  method: 'alpha_shape' | 'convex_hull' | 'degenerate';
  n_triangles: number; fallback_to_convex_hull: boolean; polygon: number[][];
};
/** 三维 Delaunay SRV。depth_reliable=false 时事件深度多半是投影伪深度, 体积不可直接采信。 */
export type InvSrv = {
  srv_1e4m3: number; alpha_m: number | null;
  method: 'alpha_shape' | 'convex_hull' | 'degenerate';
  n_tetrahedra: number; fallback_to_convex_hull: boolean;
  depth_reliable: boolean; reason: string | null;
  depth_span_m: number; horizontal_span_m: number; span_ratio: number | null;
};
export type InvStage = {
  fractures: InvFracture[]; sra_1e4m2: number; events: number[][];
  sra_alpha: InvSraAlpha; srv: InvSrv;
  metrics: { n_fractures: number; n_events: number; inlier_ratio: number; fit_rms_m: number | null };
};
export type InvResult = {
  source: 'upload' | 'demo';
  summary: { n_stages: number; n_events: number; n_fractures: number;
             mean_fit_rms_m: number | null; total_sra_1e4m2: number;
             total_sra_alpha_1e4m2: number; total_srv_1e4m3: number;
             srv_depth_reliable: boolean };
  stages: Record<string, InvStage>;
  well?: number[][] | null;
};
export const inversionAPI = {
  run: (file?: File, params?: Record<string, number>) => {
    // 无文件时发 JSON (空 multipart 无 boundary 会被后端 400)
    if (!file) {
      return api.post<InvResult>('/inversion/run/', params || {}, { timeout: 120000 });
    }
    const form = new FormData();
    form.append('file', file);
    Object.entries(params || {}).forEach(([k, v]) => form.append(k, String(v)));
    // 不手写 Content-Type, 让浏览器自动带 boundary
    return api.post<InvResult>('/inversion/run/', form, { timeout: 120000 });
  },
};

export default api;
